/**
 * <promo-vimeo data-vimeo-id="123456789" data-vimeo-hash="abcdef">
 *   <img class="collection-promo-banner__poster" ...>
 *   <div class="promo-vimeo__loader">...</div>
 * </promo-vimeo>
 *
 * Lazily injects a muted, looping Vimeo background iframe once the element
 * scrolls into view, and swaps the poster/loader out for the video once
 * playback actually starts (confirmed via Vimeo's postMessage API, not just
 * the iframe's own `load` event, which fires long before the video is
 * playable). If no `playing`/`timeupdate` event arrives within
 * READY_TIMEOUT of injecting the iframe (embed refused by Vimeo's domain or
 * privacy settings, plan limits, slow network), it gives up with `is-error`:
 * the poster stays and the iframe is removed, so shoppers never see
 * Vimeo's error screen or a black box. Being a custom element, it
 * re-initialises itself automatically whenever the collection grid is
 * replaced wholesale (facets.js section re-render) or new pages are appended
 * (load-more.js), so no extra re-init hook is needed.
 *
 * The video plays even when prefers-reduced-motion is set (it's a silent,
 * decorative background loop); only the loader's own animations respect it.
 */
if (!customElements.get('promo-vimeo')) {
  const PLAYER_ORIGIN = 'https://player.vimeo.com';
  // Generous because the clock covers the player document, its JS, config and
  // the first frame on slow mobile; the poster covers the wait.
  const READY_TIMEOUT = 12000;

  customElements.define(
    'promo-vimeo',
    class PromoVimeo extends HTMLElement {
      connectedCallback() {
        this.vimeoId = this.dataset.vimeoId;
        if (!this.vimeoId) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.loadVideo();
                this.observer.disconnect();
              }
            });
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.teardownMessaging();
      }

      loadVideo() {
        if (this.iframe) return;

        this.classList.add('is-loading');

        let src =
          `${PLAYER_ORIGIN}/video/${this.vimeoId}` +
          '?background=1&autoplay=1&loop=1&muted=1&autopause=0&playsinline=1&dnt=1&app_id=122963';
        if (this.dataset.vimeoHash) {
          src += `&h=${encodeURIComponent(this.dataset.vimeoHash)}`;
        }

        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        iframe.setAttribute('title', 'Promotional video');
        iframe.setAttribute('tabindex', '-1');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.addEventListener('load', () => this.onIframeLoad());

        // Listen before the iframe is attached: Vimeo can post its `ready`
        // event before the iframe's own `load` fires, and missing it would
        // leave the cover-crop on the 16:9 fallback (letterboxed video).
        this.messageHandler = (event) => this.handlePlayerMessage(event);
        window.addEventListener('message', this.messageHandler);

        this.appendChild(iframe);
        this.iframe = iframe;

        // Started here rather than on iframe `load`, so a player that never
        // loads at all also ends in the poster fallback.
        this.readyFallback = window.setTimeout(() => this.markError(), READY_TIMEOUT);
      }

      onIframeLoad() {
        // The iframe firing `load` only means the player chrome is ready,
        // not that the video is decoded and playing, so wait for Vimeo's
        // own player events before revealing it. Subscribe here as well as
        // on `ready`, in case `ready` came and went before we listened
        // (re-subscribing is harmless).
        this.subscribeToPlayer();
      }

      subscribeToPlayer() {
        this.postToPlayer({ method: 'addEventListener', value: 'playing' });
        this.postToPlayer({ method: 'addEventListener', value: 'timeupdate' });
        // Fetch the source video's real dimensions so the cover-crop math
        // in CSS (--promo-video-ratio) can use its actual aspect ratio
        // instead of assuming 16:9 (see component-collection-promo.css).
        this.postToPlayer({ method: 'getVideoWidth' });
        this.postToPlayer({ method: 'getVideoHeight' });
      }

      handlePlayerMessage(event) {
        if (event.origin !== PLAYER_ORIGIN || !this.iframe || event.source !== this.iframe.contentWindow) {
          return;
        }

        let data;
        try {
          data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch (e) {
          return;
        }
        if (!data) return;

        // Method call replies (getVideoWidth/getVideoHeight) come back with
        // `method` set instead of `event`, so they're handled before the
        // event-only branch below.
        if (data.method === 'getVideoWidth') {
          this.videoWidth = data.value;
          this.applyVideoRatio();
          return;
        }
        if (data.method === 'getVideoHeight') {
          this.videoHeight = data.value;
          this.applyVideoRatio();
          return;
        }

        if (!data.event) return;

        if (data.event === 'ready') {
          this.subscribeToPlayer();
        } else if (data.event === 'playing' || data.event === 'timeupdate') {
          this.markPlaying();
        } else if (data.event === 'error') {
          this.markError();
        }
      }

      applyVideoRatio() {
        if (!this.videoWidth || !this.videoHeight || this.ratioApplied) return;
        this.ratioApplied = true;
        this.style.setProperty('--promo-video-ratio', this.videoWidth / this.videoHeight);
        this.maybeTeardownMessaging();
      }

      postToPlayer(message) {
        if (!this.iframe || !this.iframe.contentWindow) return;
        this.iframe.contentWindow.postMessage(JSON.stringify(message), PLAYER_ORIGIN);
      }

      markPlaying() {
        if (this.classList.contains('is-playing')) return;
        this.classList.remove('is-loading', 'is-error');
        this.classList.add('is-playing');
        if (this.readyFallback) {
          window.clearTimeout(this.readyFallback);
          this.readyFallback = null;
        }
        this.maybeTeardownMessaging();
      }

      // Final: messaging is torn down and the iframe removed, so a hidden player
      // stops downloading/decoding video and a late `playing` event can't swap
      // the poster out afterwards.
      markError() {
        if (this.classList.contains('is-playing')) return;
        this.classList.remove('is-loading');
        this.classList.add('is-error');
        this.teardownMessaging();
        if (this.iframe) {
          this.iframe.src = 'about:blank';
          this.iframe.remove();
          this.iframe = null;
        }
      }

      // The message listener stays attached after playback starts until the
      // video-dimension replies have also arrived (or teardownMessaging is
      // forced, e.g. on error/disconnect), so an early "playing"/"timeupdate"
      // event can't cut off the getVideoWidth/getVideoHeight replies.
      maybeTeardownMessaging() {
        if (this.classList.contains('is-playing') && this.ratioApplied) {
          this.teardownMessaging();
        }
      }

      teardownMessaging() {
        if (this.messageHandler) {
          window.removeEventListener('message', this.messageHandler);
          this.messageHandler = null;
        }
        if (this.readyFallback) {
          window.clearTimeout(this.readyFallback);
          this.readyFallback = null;
        }
      }
    }
  );
}
